import { useState } from 'react';
import {
  Key,
  Plus,
  Search,
  MoreVertical,
  Eye,
  RefreshCw,
  Ban,
  Trash2,
  Copy,
  Check,
} from 'lucide-react';
import type { ApiKey } from '@yawi/types';
import { Button, Input, Table, Badge, Card, CardHeader, Modal, Pagination } from '@/core/ui/components';
import { useConfirmation } from '@/app/providers';
import { useApiKeys } from '../hooks/useApiKeys';
import { ApiKeyFormModal } from '../components/ApiKeyFormModal';

const STATUS_BADGES: Record<string, { variant: 'success' | 'warning' | 'error' | 'default'; label: string }> = {
  active: { variant: 'success', label: 'Active' },
  revoked: { variant: 'error', label: 'Revoked' },
  expired: { variant: 'warning', label: 'Expired' },
};

export function ApiKeyListPage() {
  const { confirm } = useConfirmation();
  const {
    apiKeys,
    isLoading,
    pagination,
    search,
    setSearch,
    goToPage,
    revokeApiKey,
    deleteApiKey,
    regenerateApiKey,
    refetch,
  } = useApiKeys();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [newKeyModal, setNewKeyModal] = useState<{ isOpen: boolean; key: string | null }>({
    isOpen: false,
    key: null,
  });
  const [copied, setCopied] = useState(false);

  const handleRevoke = async (apiKey: ApiKey) => {
    const confirmed = await confirm({
      title: 'Revoke API Key',
      message: `Are you sure you want to revoke "${apiKey.name}"? This action cannot be undone and any applications using this key will stop working.`,
      variant: 'warning',
      confirmText: 'Revoke',
    });

    if (confirmed) {
      await revokeApiKey(apiKey.id);
    }
  };

  const handleDelete = async (apiKey: ApiKey) => {
    const confirmed = await confirm({
      title: 'Delete API Key',
      message: `Are you sure you want to delete "${apiKey.name}"? This action cannot be undone.`,
      variant: 'danger',
      confirmText: 'Delete',
    });

    if (confirmed) {
      await deleteApiKey(apiKey.id);
    }
  };

  const handleRegenerate = async (apiKey: ApiKey) => {
    const confirmed = await confirm({
      title: 'Regenerate API Key',
      message: `Are you sure you want to regenerate "${apiKey.name}"? The old key will stop working immediately.`,
      variant: 'warning',
      confirmText: 'Regenerate',
    });

    if (confirmed) {
      const newKey = await regenerateApiKey(apiKey.id);
      if (newKey) {
        setNewKeyModal({ isOpen: true, key: newKey });
      }
    }
  };

  const handleCopyKey = async () => {
    if (newKeyModal.key) {
      await navigator.clipboard.writeText(newKeyModal.key);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const columns = [
    {
      key: 'name',
      header: 'Name',
      render: (apiKey: ApiKey) => (
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
            <Key className="w-5 h-5 text-amber-600 dark:text-amber-400" />
          </div>
          <div>
            <p className="font-medium text-theme">{apiKey.name}</p>
            <p className="text-sm text-theme-tertiary font-mono">
              {apiKey.keyPreview}...
            </p>
          </div>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (apiKey: ApiKey) => {
        const badge = STATUS_BADGES[apiKey.status] || STATUS_BADGES.active;
        return <Badge variant={badge.variant}>{badge.label}</Badge>;
      },
    },
    {
      key: 'scopes',
      header: 'Scopes',
      render: (apiKey: ApiKey) => (
        <div className="flex flex-wrap gap-1">
          {apiKey.scopes.slice(0, 2).map((scope) => (
            <Badge key={scope} variant="default" size="sm">
              {scope}
            </Badge>
          ))}
          {apiKey.scopes.length > 2 && (
            <Badge variant="default" size="sm">
              +{apiKey.scopes.length - 2}
            </Badge>
          )}
        </div>
      ),
    },
    {
      key: 'lastUsed',
      header: 'Last Used',
      render: (apiKey: ApiKey) => (
        <span className="text-theme-secondary">
          {apiKey.lastUsedAt
            ? new Date(apiKey.lastUsedAt).toLocaleDateString()
            : 'Never'}
        </span>
      ),
    },
    {
      key: 'expiresAt',
      header: 'Expires',
      render: (apiKey: ApiKey) => (
        <span className="text-theme-secondary">
          {apiKey.expiresAt
            ? new Date(apiKey.expiresAt).toLocaleDateString()
            : 'Never'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      className: 'w-12',
      render: (apiKey: ApiKey) => (
        <div className="relative">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setOpenMenuId(openMenuId === apiKey.id ? null : apiKey.id);
            }}
            className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          >
            <MoreVertical className="w-4 h-4 text-theme-secondary" />
          </button>

          {openMenuId === apiKey.id && (
            <>
              <div
                className="fixed inset-0 z-10"
                onClick={() => setOpenMenuId(null)}
              />
              <div className="absolute right-0 top-full mt-1 w-48 bg-surface rounded-lg border border-theme shadow-lg z-20 py-1">
                <button
                  onClick={() => {
                    // View stats
                    setOpenMenuId(null);
                  }}
                  className="w-full flex items-center gap-2 px-4 py-2 text-sm text-theme hover:bg-slate-100 dark:hover:bg-slate-800"
                >
                  <Eye className="w-4 h-4" />
                  View Stats
                </button>
                {apiKey.status === 'active' && (
                  <>
                    <button
                      onClick={() => {
                        handleRegenerate(apiKey);
                        setOpenMenuId(null);
                      }}
                      className="w-full flex items-center gap-2 px-4 py-2 text-sm text-theme hover:bg-slate-100 dark:hover:bg-slate-800"
                    >
                      <RefreshCw className="w-4 h-4" />
                      Regenerate
                    </button>
                    <button
                      onClick={() => {
                        handleRevoke(apiKey);
                        setOpenMenuId(null);
                      }}
                      className="w-full flex items-center gap-2 px-4 py-2 text-sm text-amber-600 hover:bg-slate-100 dark:hover:bg-slate-800"
                    >
                      <Ban className="w-4 h-4" />
                      Revoke
                    </button>
                  </>
                )}
                <div className="border-t border-theme my-1" />
                <button
                  onClick={() => {
                    handleDelete(apiKey);
                    setOpenMenuId(null);
                  }}
                  className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-slate-100 dark:hover:bg-slate-800"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete
                </button>
              </div>
            </>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader
          title="API Keys"
          description="Manage API keys for tenant integrations"
          action={
            <Button
              leftIcon={<Plus className="w-4 h-4" />}
              onClick={() => setShowCreateModal(true)}
            >
              Create API Key
            </Button>
          }
        />

        <div className="mb-6">
          <Input
            placeholder="Search API keys..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            leftIcon={<Search className="w-4 h-4" />}
            className="max-w-sm"
          />
        </div>

        <Table
          columns={columns}
          data={apiKeys || []}
          keyExtractor={(key) => key.id}
          isLoading={isLoading}
          emptyMessage="No API keys found"
        />

        <Pagination meta={pagination} onPageChange={goToPage} />
      </Card>

      {/* Create Modal */}
      {showCreateModal && (
        <ApiKeyFormModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={(key) => {
            setShowCreateModal(false);
            setNewKeyModal({ isOpen: true, key });
            refetch();
          }}
        />
      )}

      {/* New Key Display Modal */}
      {newKeyModal.isOpen && newKeyModal.key && (
        <Modal
          isOpen
          onClose={() => setNewKeyModal({ isOpen: false, key: null })}
          title="API Key Created"
          size="md"
          footer={
            <Button onClick={() => setNewKeyModal({ isOpen: false, key: null })}>
              Done
            </Button>
          }
        >
          <div className="space-y-4">
            <div className="p-4 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
              <p className="text-sm text-amber-800 dark:text-amber-200">
                Make sure to copy your API key now. You won't be able to see it again!
              </p>
            </div>

            <div className="relative">
              <code className="block p-4 bg-slate-100 dark:bg-slate-800 rounded-lg text-sm font-mono break-all">
                {newKeyModal.key}
              </code>
              <button
                onClick={handleCopyKey}
                className="absolute top-2 right-2 p-2 rounded-lg bg-white dark:bg-slate-700 shadow hover:bg-slate-50 dark:hover:bg-slate-600 transition-colors"
              >
                {copied ? (
                  <Check className="w-4 h-4 text-green-600" />
                ) : (
                  <Copy className="w-4 h-4 text-theme-secondary" />
                )}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
